import { Suspense, memo, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';

import { getTranslations } from '@/services/ws-translate/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Button, Icons, Loader, Notification, useNotification } from '@/js/libs/ui/src/V2';
import { Roles, useRolesMatch } from '@/js/libs/use-auth';
import { isCancelError, useLazyRequest } from '@/js/libs/use-request';

import { SearchFilter } from 'src/components/TableFilters/components/SearchFilter.tsx';
import { DEV_MODE } from 'src/constants.ts';
import { useTranslateModeContext } from './context.ts';
import { useTranslationData } from './hooks/useTranslationData.ts';
import { EmptyState, OverlayContainer, SearchContainer, SectionContainer, ToggleButton, ToggleIcon, TranslationList } from './styled.ts';
import { TranslationEditor } from './TranslationEditor.tsx';
import { TranslationListItem } from './TranslationListItem.tsx';
import type { TrackedTranslation } from './types.ts';

export const TranslateModeOverlay = memo(function TranslateModeOverlay() {
  const [t] = useTranslation(['common']);
  const theme = useTheme();
  const notify = useNotification();
  const { isEnabled, isOverlayOpen, trackedKeys, openOverlay, closeOverlay, editorModalState } = useTranslateModeContext();
  const hasTranslateModeAccess = useRolesMatch({ roles: Roles.ADMIN });

  const [searchQuery, setSearchQuery] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const { translations } = useTranslationData(trackedKeys);

  // queryDefaults: false keeps the default page and limit out of the request, so ws-translate returns all of the
  // namespace's keys that contain the clicked key, the exact key included
  const getTranslationsRequest = useLazyRequest({ apiMethod: getTranslations, queryDefaults: false });
  const isTranslationLoading = useSubscribe(getTranslationsRequest.pending$);

  const filteredTranslations = useMemo(() => {
    if (!searchQuery.trim()) {
      return translations;
    }

    const query = searchQuery.toLowerCase();
    return translations.filter((item) => item.fullKey.toLowerCase().includes(query) || item.value.toLowerCase().includes(query));
  }, [translations, searchQuery]);

  const handleTranslationClick = useCallbackRef(async ({ namespace, key }: TrackedTranslation) => {
    closeOverlay();
    try {
      const { data } = await getTranslationsRequest.load({ namespace, keyFilter: key });
      const translation = data.items.find((item) => item.key === key);

      if (!translation) {
        notify.error({ title: t('common:ERROR'), body: t('messages:TRANSLATION_NOT_FOUND') });
        openOverlay();
        return;
      }

      editorModalState.open(translation);
    } catch (error) {
      // failure is surfaced by the global onFail handler
      if (!isCancelError(error)) {
        openOverlay();
      }
    }
  });

  const handleButtonClick = useCallbackRef(() => {
    if (isOverlayOpen) {
      closeOverlay();
    } else {
      setSearchQuery('');
      openOverlay();
    }
  });

  const handleClickOutside = useCallbackRef((e: MouseEvent) => {
    if (!isOverlayOpen) {
      return;
    }

    const target = e.target as Node;
    if (!overlayRef.current?.contains(target) && !toggleRef.current?.contains(target)) {
      closeOverlay();
    }
  });

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (!isEnabled) {
      getTranslationsRequest.abort();
    }
  }, [isEnabled, getTranslationsRequest]);

  return isEnabled && hasTranslateModeAccess
    ? createPortal(
        <>
          <ToggleButton
            ref={toggleRef}
            $isActive={isOverlayOpen}
            disabled={isTranslationLoading}
            onClick={handleButtonClick}
            data-testid='translate-mode-toggle'>
            {isTranslationLoading ? <Loader content size='20px' fill={theme.colors.grey00_1} /> : <ToggleIcon>{'T'}</ToggleIcon>}
          </ToggleButton>

          <CSSTransition in={isOverlayOpen} timeout={200} unmountOnExit>
            <OverlayContainer ref={overlayRef} $visible={isOverlayOpen} data-testid='translate-mode-overlay'>
              <SectionContainer
                title={t('common:TRANSLATIONS')}
                tag={trackedKeys.length.toString()}
                actions={
                  <Button ghost iconOnly onClick={closeOverlay} data-testid='close-overlay-button'>
                    <Icons.Close size={20} color={theme.colors.grey100_1} />
                  </Button>
                }>
                {DEV_MODE && (
                  <SearchContainer>
                    <Notification type='warning' body={t('common:DEV_ENV_ALERT_MESSAGE')} fullWidth uncloseable />
                  </SearchContainer>
                )}
                <SearchContainer>
                  <SearchFilter fullWidth filter={searchQuery} onChange={setSearchQuery} />
                </SearchContainer>

                <TranslationList>
                  {filteredTranslations.length === 0 ? (
                    <EmptyState>{trackedKeys.length === 0 ? t('common:NO_DATA') : t('common:NO_MATCHES_FOUND')}</EmptyState>
                  ) : (
                    filteredTranslations.map((translation) => (
                      <TranslationListItem key={translation.fullKey} translation={translation} onClick={handleTranslationClick} />
                    ))
                  )}
                </TranslationList>
              </SectionContainer>
            </OverlayContainer>
          </CSSTransition>

          {/* Waits here for the namespaces the editor loads on demand: the overlay sits above the content area, where the
              nearest Suspense boundary is the app's own and its fallback replaces the whole app. */}
          <Suspense>
            <TranslationEditor />
          </Suspense>
        </>,
        document.body,
      )
    : null;
});
